import { StatusChip } from "@/shared/components/StatusChip";
import { useLiveStatus } from "@/shared/live/LiveProvider";
import type { LiveStatus } from "@/shared/live/useTenantStream";
import { cn } from "@/shared/ui/cn";

interface LiveIndicatorProps {
  /** Devices currently online; undefined while unknown. */
  onlineCount?: number;
  /** Devices paired at all — "1 of 2" says more than "1" and is the board's wording. */
  totalCount?: number;
}

/**
 * Board `1a`: a bordered 24 px pill holding two facts separated by a hairline — the CHANNEL on the
 * left ("Live", pulsing) and what it is reporting on the right ("1 of 2 terminals online"). They are
 * different things and the rule is answered separately: the stream can be down while every terminal
 * is up, and a single run-on chip hid that.
 */
function chipFor(status: LiveStatus, onlineCount: number | undefined, total: number | undefined) {
  if (status === "reconnecting") {
    return <StatusChip tone="warning" shape="hollow" label="Reconnecting…" />;
  }
  if (status === "limited" || status === "unavailable") {
    return <StatusChip tone="neutral" shape="hollow" label="Live updates off" />;
  }
  const degraded = total !== undefined && onlineCount !== undefined && onlineCount < total;
  const healthy = total !== undefined && total > 0 && onlineCount === total;
  return (
    <span
      title="Live connection and terminal availability"
      className="inline-flex h-6 items-center gap-1.5 rounded-sm border border-border px-2"
    >
      <span
        aria-hidden
        className={cn(
          "inline-block size-1.5 rounded-full",
          degraded ? "bg-warning" : healthy ? "bg-success" : "bg-accent motion-safe:animate-live",
        )}
      />
      <span className="text-caption font-medium text-fg">Live</span>
      {onlineCount !== undefined ? (
        <>
          <span aria-hidden className="h-3 w-px bg-border" />
          <span
            className={cn(
              "whitespace-nowrap text-caption font-medium",
              degraded ? "text-warning-fg" : "text-fg-muted",
            )}
          >
            {total !== undefined
              ? `${onlineCount} of ${total} ${total === 1 ? "terminal" : "terminals"} online`
              : `${onlineCount} ${onlineCount === 1 ? "terminal" : "terminals"} online`}
          </span>
        </>
      ) : null}
    </span>
  );
}

/**
 * "Live · 3 devices online" (design-system §4). The count is the devices query; the word in front of
 * it is the SSE channel (M8): "Reconnecting…" while the stream is down — a latency symptom, never a
 * data-loss one, because the polling fallback takes over — and back to Live when it opens.
 *
 * The wrapper is a screenshot hook and nothing else. The visual gate masks this chip (what it says is
 * live state, not design), and Playwright paints a mask over the element's own box — as wide as the
 * text, so "0 devices" and "1 device" masked different widths and a terminal going online failed every
 * screen with a top bar. The gate pins the wrapper to one width before it measures (e2e/fixtures.ts);
 * in the product the wrapper has no size of its own.
 */
export function LiveIndicator({ onlineCount, totalCount }: LiveIndicatorProps) {
  const { status } = useLiveStatus();
  return (
    <span data-testid="live-indicator" className="inline-flex min-w-0">
      {chipFor(status, onlineCount, totalCount)}
    </span>
  );
}
